import { api, Banner, Empty, Metric, PageHead, stackOnMobile, ToggleGroup, useAsync, useStoredState } from "@getaways/extranet-ui";
import { useState } from "react";
import { useCan } from "../lib/access";
import { filterQuery, FilterBar, useFilters, useOptions } from "../lib/filters";
import { BRANDS, eur, int, pct } from "../lib/format";

type Group = "hotel" | "supplier" | "destination";
type View = Group | "bookings";

interface SummaryRow {
  key: string;
  label: string;
  detail: string | null;
  bookings: number;
  rooms: number;
  roomNights: number;
  sellEurMinor: number;
  costEurMinor: number | null;
  marginEurMinor: number | null;
  adrEurMinor: number | null;
  cancelled: number;
  suppliers: string[];
}

interface ItemRow {
  orderId: string;
  itemId: string;
  hotelName: string | null;
  name: string;
  destination: string | null;
  country: string | null;
  supplier: string;
  supplierReference: string | null;
  room: string | null;
  board: string | null;
  serviceStart: string | null;
  serviceEnd: string | null;
  rooms: number | null;
  nights: number | null;
  roomNights: number | null;
  sellEurMinor: number | null;
  costEurMinor: number | null;
  marginEurMinor: number | null;
  adrEurMinor: number | null;
  brand: string;
  cancelled: boolean;
  origin: string;
}

const PAGE = 100;
const GROUP_LABEL: Record<Group, string> = { hotel: "Hotel", supplier: "Bed bank / supplier", destination: "Destination" };

/** Every booked hotel (docs/36 §7): by hotel, bed bank or destination, or booking by booking. */
export function HotelsPage() {
  const can = useCan();
  const money = can("money:read");
  const [filters, setFilters] = useFilters("hotels");
  const options = useOptions();
  const [view, setView] = useStoredState<View>("reports.hotels.view", "hotel");
  const [offset, setOffset] = useState(0);
  const q = filterQuery({ ...filters, status: filters.status }, { verticals: "hotel" });

  const summary = useAsync(() => (view === "bookings" ? Promise.resolve(undefined) : api<{ rows: SummaryRow[] }>("GET", `/v1/hotels/summary?${q}&group=${view}`)), [q, view]);
  const items = useAsync(() => (view === "bookings" ? api<{ rows: ItemRow[]; total: number }>("GET", `/v1/items?${q}&limit=${PAGE}&offset=${offset}&sort=serviceStart&dir=asc`) : Promise.resolve(undefined)), [q, view, offset]);

  const rows = summary.data?.rows ?? [];
  const totals = rows.reduce(
    (t, r) => ({ bookings: t.bookings + r.bookings, roomNights: t.roomNights + r.roomNights, sell: t.sell + r.sellEurMinor, margin: r.marginEurMinor === null || t.margin === null ? null : t.margin + r.marginEurMinor }),
    { bookings: 0, roomNights: 0, sell: 0, margin: 0 as number | null },
  );
  const error = summary.error ?? items.error;

  return (
    <>
      <PageHead title="Hotels" sub="Every booked hotel, with its supplier, nights and price." />
      {error && <Banner notice={{ tone: "bad", text: error }} />}
      {view !== "bookings" && summary.data && (
        <div className="metrics">
          <Metric label="Hotel bookings" value={int(totals.bookings)} />
          <Metric label="Room nights" value={int(totals.roomNights)} />
          <Metric label="Sold (EUR)" value={eur(totals.sell)} />
          <Metric label="Average daily rate" value={eur(totals.roomNights ? Math.round(totals.sell / totals.roomNights) : null)} />
          {money && <Metric label="Margin" value={eur(totals.margin)} hint={pct(totals.margin, totals.sell)} />}
        </div>
      )}
      <div className="card" style={{ padding: 0 }}>
        <FilterBar filters={filters} onChange={(p) => { setFilters(p); setOffset(0); }} options={options} />
        <div className="filters">
          <ToggleGroup<View> label="Show" value={view} onValueChange={(v) => { setView(v); setOffset(0); }} items={[{ value: "hotel", label: "By hotel" }, { value: "supplier", label: "By bed bank" }, { value: "destination", label: "By destination" }, { value: "bookings", label: "Every booking" }]} />
        </div>
        {view !== "bookings" && (summary.data && rows.length === 0 ? <Empty>No hotel booked in this period.</Empty> : (
          <table ref={stackOnMobile} className="table stack-mobile">
            <thead>
              <tr>
                <th>{GROUP_LABEL[view]}</th>
                {view === "hotel" && <th>Supplier</th>}
                <th className="num">Bookings</th><th className="num">Rooms</th><th className="num">Room nights</th><th className="num">Sold</th><th className="num">ADR</th>
                {money && <><th className="num">Cost</th><th className="num">Margin</th><th className="num">Margin %</th></>}
                <th className="num">Cancelled</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td>{r.label}{r.detail && <div className="small muted">{r.detail}</div>}</td>
                  {view === "hotel" && <td className="small">{r.suppliers.join(", ")}</td>}
                  <td className="num">{int(r.bookings)}</td><td className="num">{int(r.rooms)}</td><td className="num">{int(r.roomNights)}</td>
                  <td className="num">{eur(r.sellEurMinor)}</td><td className="num">{eur(r.adrEurMinor)}</td>
                  {money && <><td className="num">{eur(r.costEurMinor)}</td><td className="num">{eur(r.marginEurMinor)}</td><td className="num">{pct(r.marginEurMinor, r.sellEurMinor)}</td></>}
                  <td className="num">{r.cancelled ? int(r.cancelled) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
        {view === "bookings" && (items.data && items.data.rows.length === 0 ? <Empty>No hotel booked in this period.</Empty> : (
          <>
            <table ref={stackOnMobile} className="table stack-mobile">
              <thead>
                <tr>
                  <th>Hotel</th><th>Stay</th><th>Room</th><th>Supplier</th><th>Brand</th><th className="num">Room nights</th><th className="num">Sold</th><th className="num">ADR</th>
                  {money && <><th className="num">Cost</th><th className="num">Margin</th></>}
                </tr>
              </thead>
              <tbody>
                {(items.data?.rows ?? []).map((r) => (
                  <tr key={`${r.orderId}:${r.itemId}`}>
                    <td>{r.hotelName ?? r.name}{r.cancelled && <span className="small muted"> · cancelled</span>}<div className="small muted">{[r.destination, r.country].filter(Boolean).join(", ")}{r.origin === "legacy" ? " · legacy" : ""}</div></td>
                    <td className="small">{r.serviceStart ?? "—"} → {r.serviceEnd ?? "—"}<div className="muted">{r.nights ?? "?"} nights</div></td>
                    <td className="small">{r.room ?? "—"}{r.board && <div className="muted">{r.board}</div>}</td>
                    <td className="small">{r.supplier}{r.supplierReference && <div className="muted mono">{r.supplierReference}</div>}</td>
                    <td className="small">{BRANDS[r.brand] ?? r.brand}</td>
                    <td className="num">{int(r.roomNights)}</td><td className="num">{eur(r.sellEurMinor)}</td><td className="num">{eur(r.adrEurMinor)}</td>
                    {money && <><td className="num">{eur(r.costEurMinor)}</td><td className="num">{eur(r.marginEurMinor)}</td></>}
                  </tr>
                ))}
              </tbody>
            </table>
            {items.data && items.data.total > PAGE && (
              <div className="pager">
                <span className="small muted">{offset + 1}–{Math.min(offset + PAGE, items.data.total)} of {items.data.total}</span>
                <span className="spacer" />
                <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</button>
                <button disabled={offset + PAGE >= items.data.total} onClick={() => setOffset(offset + PAGE)}>Next</button>
              </div>
            )}
          </>
        ))}
      </div>
    </>
  );
}
